import React, { useState, useEffect } from 'react';
import { CustomerUpload, Box } from '@wegroup/design-system';
import { handleResponseLanguage } from '../../../../../i18n';
import { CreateSignedDistUploadId } from '../../../../common/src/legacy/services/apiRouterService';
import { useTranslation } from 'react-i18next';
import { SmtFile } from '../../../../../types/Form';
import { FlowsQuestion } from '../../../../flows/src/types/FlowsTypes';
import useFlowInputComponent from '../../../../flows/src/hooks/useFlowsInputComponent';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';
interface Props {
  question: FlowsQuestion;
}

const LinkInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { distribution } = useAuth();

  const {
    field: { value, onBlur },
    error,
  } = useFlowInputComponent({ question });

  const inputValue = value as SmtFile;

  const { t } = useTranslation();
  const [file, setFile] = useState<File | undefined>();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(error?.message);

  useEffect(() => {
    setErrorMsg(error?.message);
  }, [error]);

  const supportedFormats = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
  ];

  const uploadFile = async (file: File) => {
    const [resp, status] = await CreateSignedDistUploadId(distribution?.id, {
      filename: file.name,
    });
    if (status !== 200) {
      try {
        await fetch(resp.link, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/pdf' },
          body: new Blob([file], { type: file.type }),
        });

        return {
          document_id: resp.document_id,
          location: resp.location,
          filename: file.name,
          size: file.size,
          filetype: file.type,
          description: handleResponseLanguage(question?.title),
          public_bucket_link: resp.public_bucket_link,
          public_url: resp.public_url,
        };
      } catch (err) {
        return Promise.reject();
      }
    } else {
      return Promise.reject();
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setErrorMsg(undefined);
      setFile(file);
      const uploadedFile = await uploadFile(file);
      onBlur({ name: question.id, value: uploadedFile });
    } catch {
      setErrorMsg(t('Upload of document has failed'));
    }
  };

  const clearFile = () => {
    setFile(undefined);
    setErrorMsg(undefined);
    onBlur({ name: question.id, value: undefined });
  };

  return (
    <Box w="100%" flexGrow={1} maxW="790px" mx="auto">
      <CustomerUpload
        accept={supportedFormats}
        onFileAccepted={handleUpload}
        file={
          file || inputValue
            ? ({ name: inputValue?.filename } as File)
            : undefined
        }
        onRemove={clearFile}
        errorMsg={errorMsg}
        dataTestId={`RISK-ANALYSIS_LINK-INPUT_${question.id
          .toUpperCase()
          .replaceAll('.', '-')}`}
      >
        {handleResponseLanguage(question?.title)}
      </CustomerUpload>
    </Box>
  );
};

export default LinkInputComponent;
